import React, { useMemo, useState } from 'react';
import { Box, Button, Text, VStack } from '@chakra-ui/react';

function InstructionSteps({ steps }) {
  return (
    <VStack align="stretch" spacing={1} mt={2}>
      {steps.map((step, index) => (
        <Text key={step} fontSize="sm" color="gray.500">
          {index + 1}. {step}
        </Text>
      ))}
    </VStack>
  );
}

export function IosInstallInstructions() {
  return (
    <Box>
      <Text fontWeight="semibold">Add Ekkos to your Home Screen</Text>
      <InstructionSteps
        steps={[
          'Open this page in Safari on your iPhone or iPad.',
          'Tap the Share button (square with an arrow).',
          "Choose 'Add to Home Screen'.",
          "Tap 'Add' to finish installing the app.",
        ]}
      />
    </Box>
  );
}

export function AndroidInstallInstructions({ canPromptInstall, onPromptInstall }) {
  const [installStatus, setInstallStatus] = useState(null);
  const [isPrompting, setIsPrompting] = useState(false);

  const statusCopy = useMemo(() => {
    switch (installStatus) {
      case 'accepted':
        return 'Install prompt sent. Launch the installed app once it finishes to complete this step.';
      case 'dismissed':
        return 'Install canceled. You can try again or use the browser menu to install later.';
      case 'unavailable':
        return "Could not open the install prompt. Use the browser menu and look for 'Install app'.";
      default:
        return null;
    }
  }, [installStatus]);

  const handlePromptInstall = async () => {
    if (!onPromptInstall) return;
    setIsPrompting(true);
    try {
      const outcome = await onPromptInstall();
      setInstallStatus(outcome);
    } finally {
      setIsPrompting(false);
    }
  };

  return (
    <Box>
      <Text fontWeight="semibold">Install the app for faster access</Text>
      {canPromptInstall ? (
        <Button
          size="sm"
          colorScheme="blue"
          mt={2}
          onClick={handlePromptInstall}
          isLoading={isPrompting}
        >
          Install the app
        </Button>
      ) : (
        <InstructionSteps
          steps={[
            'Open the browser menu (three dots) in Chrome or Edge.',
            "Tap 'Install app' or 'Add to Home screen'.",
            'Open Ekkos from your app drawer after it installs.',
          ]}
        />
      )}
      {canPromptInstall && (
        <Text mt={2} fontSize="sm" color="gray.500">
          If the button is missing, open the browser menu and choose 'Install app'.
        </Text>
      )}
      {statusCopy && (
        <Text mt={2} fontSize="xs" color="gray.500">
          {statusCopy}
        </Text>
      )}
    </Box>
  );
}

export function WebInstallInstructions({ canPromptInstall, onPromptInstall }) {
  const [installStatus, setInstallStatus] = useState(null);
  const [isPrompting, setIsPrompting] = useState(false);

  const statusCopy = useMemo(() => {
    switch (installStatus) {
      case 'accepted':
        return 'Install prompt sent. Launch the standalone window after install to finish the step.';
      case 'dismissed':
        return 'Prompt dismissed. Use the browser menu or address bar install icon whenever you are ready.';
      case 'unavailable':
        return "Install prompt unavailable. Look for 'Install Ekkos' in the browser menu.";
      default:
        return null;
    }
  }, [installStatus]);

  const handlePromptInstall = async () => {
    if (!onPromptInstall) return;
    setIsPrompting(true);
    try {
      const outcome = await onPromptInstall();
      setInstallStatus(outcome);
    } finally {
      setIsPrompting(false);
    }
  };

  return (
    <Box>
      <Text fontWeight="semibold">Install the Ekkos app</Text>
      {canPromptInstall ? (
        <Button
          size="sm"
          colorScheme="blue"
          mt={2}
          onClick={handlePromptInstall}
          isLoading={isPrompting}
        >
          Install the app
        </Button>
      ) : (
        <InstructionSteps
          steps={[
            'In Chrome or Edge, look for the install icon in the address bar.',
            "Alternatively, open the browser menu and choose 'Install app'.",
            'Launch the installed app from your applications list.',
          ]}
        />
      )}
      {statusCopy && (
        <Text mt={2} fontSize="xs" color="gray.500">
          {statusCopy}
        </Text>
      )}
    </Box>
  );
}

export default function InstallAppInstructions({
  platform = 'web',
  canPromptInstall,
  promptInstall,
}) {
  if (platform === 'ios') {
    return <IosInstallInstructions />;
  }
  if (platform === 'android') {
    return (
      <AndroidInstallInstructions
        canPromptInstall={canPromptInstall}
        onPromptInstall={promptInstall}
      />
    );
  }
  return (
    <WebInstallInstructions
      canPromptInstall={canPromptInstall}
      onPromptInstall={promptInstall}
    />
  );
}
